import type {
  Authority,
  ConsentItem,
  Decision,
  MemberSettings,
  MeResponse,
  Person,
  Role,
  RoomSummary,
  DocContent,
  DocMeta,
  DocVersion,
  Draft,
  DraftPayload,
  Invite,
  Member,
  Message,
  MessageKind,
  MessageTarget,
  PostMessageResult,
  RoomState,
  StreamEvent,
  RetractResult,
  EnvLink,
  UpsertLinkRequest,
  RoomCard,
  Attachment,
} from '@cs/shared'

/**
 * The browser authenticates with the cookie set at redeem time. The token is also
 * kept in localStorage so a member who joined on another device can paste it in,
 * and so the Authorization header still works if the cookie is dropped.
 */
const TOKEN_KEY = 'cs_token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // Private mode or blocked storage: the cookie still carries the session.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* nothing to clear */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const token = getToken()
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!res.ok) {
    let code = 'error'
    let message = `${res.status} ${res.statusText}`
    try {
      const parsed = await res.json()
      code = parsed.error ?? code
      message = parsed.message ?? message
    } catch {
      /* non-JSON error body; keep the status line */
    }
    throw new ApiError(res.status, code, message)
  }
  return (await res.json()) as T
}

export const api = {
  me: () => request<MeResponse>('GET', '/v1/me'),

  rooms: () => request<{ rooms: RoomSummary[] }>('GET', '/v1/rooms'),

  consent: (accepted: ConsentItem[]) =>
    request<{ consent_version: string }>('POST', '/v1/consent', { accepted }),

  updateMySettings: (
    room: string,
    patch: { settings?: Partial<MemberSettings>; agent_nickname?: string | null },
  ) => request<Member>('POST', `/v1/rooms/${room}/me/settings`, patch),

  /** `token` is null when an existing identity simply gained another project. */
  redeem: (code: string, display_name?: string, accepted?: ConsentItem[]) =>
    request<{
      token: string | null
      person: Person
      room: { id: string; name: string }
      role: string
      joined_existing: boolean
    }>('POST', '/v1/redeem', {
      code,
      ...(display_name ? { display_name } : {}),
      accepted: accepted ?? [],
    }),

  state: (room: string) => request<RoomState>('GET', `/v1/rooms/${room}/state`),

  members: (room: string) => request<{ members: Member[] }>('GET', `/v1/rooms/${room}/members`),

  messages: (room: string, sinceSeq?: number) =>
    request<{ messages: Message[]; latest_seq: number }>(
      'GET',
      sinceSeq === undefined
        ? `/v1/rooms/${room}/messages`
        : `/v1/rooms/${room}/messages?since_seq=${sinceSeq}`,
    ),

  post: (
    room: string,
    input: {
      body: string
      kind?: MessageKind
      mentions?: string[]
      in_reply_to?: string | null
      to_role?: MessageTarget
    },
  ) => request<PostMessageResult>('POST', `/v1/rooms/${room}/messages`, input),

  answer: (messageId: string, body: string, resolve: boolean) =>
    request<PostMessageResult>('POST', `/v1/messages/${messageId}/answer`, { body, resolve }),

  resolve: (messageId: string) => request<Message>('POST', `/v1/messages/${messageId}/resolve`, {}),

  drafts: (room: string, status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending') =>
    request<{ drafts: Draft[] }>('GET', `/v1/rooms/${room}/drafts?status=${status}`),

  approve: (room: string, id: string, payload?: DraftPayload) =>
    request<Draft>('POST', `/v1/rooms/${room}/drafts/${id}/approve`, payload ? { payload } : {}),

  reject: (room: string, id: string, reason: string) =>
    request<Draft>('POST', `/v1/rooms/${room}/drafts/${id}/reject`, { reason }),

  /**
   * Upload goes out as a raw body, not multipart or base64: base64 would inflate a
   * 25MB file by a third for nothing. The name and note ride in headers, percent
   * encoded, because header values are Latin-1 and these are Japanese.
   */
  uploadFile: async (room: string, file: File, note?: string) => {
    const token = getToken()
    const res = await fetch(`/v1/rooms/${room}/files`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/octet-stream',
        'x-filename': encodeURIComponent(file.name),
        ...(note ? { 'x-note': encodeURIComponent(note) } : {}),
        // The person picked this file in their own browser; that is the review.
        'x-confirmed': '1',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string }
      throw new ApiError(res.status, body.error ?? 'error', body.message ?? `HTTP ${res.status}`)
    }
    return ((await res.json()) as { file: Attachment }).file
  },

  /**
   * Plain URL, used by <a download> and <img src>. Those cannot carry an
   * Authorization header, which is exactly why the session cookie exists.
   */
  fileUrl: (room: string, id: string) => `/v1/rooms/${room}/files/${id}/content`,

  deleteFile: (room: string, id: string) =>
    request<{ removed: string; already_downloaded_by: string[] }>(
      'POST',
      `/v1/rooms/${room}/files/${id}/delete`,
    ),

  renameRoom: (room: string, name: string) =>
    request<{ id: string; name: string }>('POST', `/v1/rooms/${room}/rename`, { name }),

  summaries: () => request<{ rooms: RoomCard[] }>('GET', '/v1/summaries'),

  upsertLink: (room: string, input: UpsertLinkRequest) =>
    request<{ link: EnvLink; warnings: unknown[] }>('POST', `/v1/rooms/${room}/links`, input),

  deleteLink: (room: string, id: string) =>
    request<{ removed: string }>('POST', `/v1/rooms/${room}/links/${id}/delete`),

  retractMessage: (room: string, id: string) =>
    request<RetractResult>('POST', `/v1/rooms/${room}/messages/${id}/retract`),

  retractReport: (room: string, id: string) =>
    request<RetractResult>('POST', `/v1/rooms/${room}/reports/${id}/retract`),

  docs: (room: string) => request<{ docs: DocMeta[] }>('GET', `/v1/rooms/${room}/docs`),

  doc: (room: string, path: string, version?: string) =>
    request<DocContent>(
      'GET',
      `/v1/rooms/${room}/doc/${path}${version ? `?version=${encodeURIComponent(version)}` : ''}`,
    ),

  docVersions: (room: string, path: string) =>
    request<{ versions: DocVersion[] }>('GET', `/v1/rooms/${room}/doc-versions/${path}`),

  docDiff: (room: string, path: string, from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    const qs = q.toString()
    return request<{ path: string; from: string | null; to: string | null; patch: string }>(
      'GET',
      `/v1/rooms/${room}/doc-diff/${path}${qs ? `?${qs}` : ''}`,
    )
  },

  proposeDoc: (
    room: string,
    path: string,
    input: { content: string; note?: string; base_version?: string | null },
  ) => request<{ status: string; draft: Draft }>('POST', `/v1/rooms/${room}/doc-propose/${path}`, input),

  decide: (room: string, input: { decision: string; rationale?: string; question_message_id?: string | null }) =>
    request<Decision>('POST', `/v1/rooms/${room}/decisions`, input),

  invites: (room: string) => request<{ invites: Invite[] }>('GET', `/v1/rooms/${room}/invites`),

  createInvite: (
    room: string,
    input: { display_name: string; org: string; role: Role; authorities: Authority[] },
  ) => request<Invite>('POST', `/v1/rooms/${room}/invites`, input),

  revokeInvite: (room: string, code: string) =>
    request<{ revoked: string }>('POST', `/v1/rooms/${room}/invites/${code}/revoke`, {}),

  updateMember: (
    room: string,
    person: string,
    patch: { role?: Role; authorities?: Authority[] },
  ) => request<Member>('POST', `/v1/rooms/${room}/members/${person}`, patch),

  removeMember: (room: string, person: string) =>
    request<{ removed: string; token_revoked: boolean; message: string }>(
      'POST',
      `/v1/rooms/${room}/members/${person}/remove`,
      {},
    ),
}

/**
 * Live updates with an automatic reconnect that resumes from the last seq seen, so a
 * dropped socket never leaves a gap in the timeline. Falls back to nothing on its own
 * — callers still refetch on reconnect via the `hello` event.
 */
/**
 * Establish the session cookie from the token we hold.
 *
 * Needed because a browser WebSocket cannot send an Authorization header — only
 * cookies. Someone who joined from the CLI and pasted their token here has a token
 * in localStorage and no cookie, so every stream connection was rejected and the
 * timeline only moved when they reloaded by hand. Idempotent: called on boot.
 */
export async function establishSession(): Promise<void> {
  try {
    await request<{ ok: boolean }>('POST', '/v1/session')
  } catch {
    /* A bad token shows up on the next real request; nothing to do here. */
  }
}

export function openStream(
  room: string,
  getSinceSeq: () => number,
  onEvent: (event: StreamEvent) => void,
  onStatus?: (connected: boolean) => void,
): () => void {
  let socket: WebSocket | null = null
  let retry = 0
  let stopped = false
  let timer: number | undefined

  const connect = () => {
    if (stopped) return
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    socket = new WebSocket(`${proto}//${location.host}/v1/rooms/${room}/stream?since_seq=${getSinceSeq()}`)

    socket.onopen = () => {
      retry = 0
      onStatus?.(true)
    }
    socket.onmessage = (ev) => {
      try {
        onEvent(JSON.parse(ev.data) as StreamEvent)
      } catch {
        /* ignore a frame we cannot parse rather than tearing down the stream */
      }
    }
    socket.onclose = (ev) => {
      if (stopped) return
      onStatus?.(false)
      // 4401 means the cookie is missing or stale — reconnecting as-is would loop
      // forever without ever saying why, which is exactly how a silently dead
      // timeline happens. Mint the cookie again before retrying.
      const reauth = ev.code === 4401 ? establishSession() : Promise.resolve()
      // Back off to 15s: a hub restart or a sleeping laptop should not hammer it.
      const delay = Math.min(1000 * 2 ** retry++, 15_000)
      timer = window.setTimeout(() => void reauth.then(connect), delay)
    }
    socket.onerror = () => socket?.close()
  }

  connect()
  return () => {
    stopped = true
    if (timer) window.clearTimeout(timer)
    socket?.close()
  }
}
